import { Checkbox } from '@mui/material';
import { Lock } from 'lucide-react';
import { useRef, useState } from 'react';
import logo from '../../../assets/logos/aye-logo.png';
import { SecurityAvatarPicker } from '../../../components/SecurityAvatarPicker';
import { REFERRAL_CODE_KEY } from '../../../constants/referral.const';
import PayNetFooter from '../../../shared/PayNetFooter';
import {
  normalizeSecurityPhrase,
  validateSecurityPhrase
} from '../../../utils/security-phrase';
import {
  AccountDetailsForm,
  AccountDetailsFormHandle
} from '../RegisterScreen/components/AccountDetailsForm';
import { useCreateAccount, useGeo } from '../api/hooks';
import {
  CreateAccountBody,
  RegistrationApiError,
  registrationApi
} from '../api/registration.api';
import { isRegisterDryRunEnabled } from '../flow/dryRun';
import type { RegistrationContext } from '../flow/types';
import { StepBanner } from './StepBanner';

const GENERIC_ERROR = 'Something went wrong. Please try again.';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AVATAR_AGAIN = 'Please pick your avatar again.';

interface CreateAccountScreenProps {
  context: RegistrationContext;
  onCreated: (result: { accessToken: string; exp: number }) => void;
  /** A test registration passed every check; no account was created. */
  onDryRun: () => void;
}

/** A server message pinned to the field value it was raised for. */
interface PinnedError {
  message: string;
  value: string;
}

const originFor = (
  context: RegistrationContext
): CreateAccountBody['origin'] => {
  let referralCode: string | undefined;
  try {
    referralCode = localStorage.getItem(REFERRAL_CODE_KEY) || undefined;
  } catch {
    referralCode = undefined;
  }
  const withReferral = referralCode ? { referralCode } : {};
  if (context.checkout) {
    return {
      kind: 'checkout',
      sessionId: context.checkout.sessionId,
      ...withReferral
    };
  }
  if (context.ongKey) {
    return { kind: 'onglaye', key: context.ongKey, ...withReferral };
  }
  if (context.externalSessionId) {
    return {
      kind: 'external',
      sessionId: context.externalSessionId,
      ...withReferral
    };
  }
  return { kind: 'direct', ...withReferral };
};

const badFieldsOf = (error: RegistrationApiError): string[] => {
  const badFields = (error.meta as { badFields?: unknown } | undefined)
    ?.badFields;
  return Array.isArray(badFields) ? (badFields as string[]) : [];
};

export function CreateAccountScreen({
  context,
  onCreated,
  onDryRun
}: Readonly<CreateAccountScreenProps>) {
  const dryRunEnabled = isRegisterDryRunEnabled();
  const [dryRunChecked, setDryRunChecked] = useState(false);
  const { prefill } = context;
  const geo = useGeo(!prefill);
  const createAccount = useCreateAccount();
  const formRef = useRef<AccountDetailsFormHandle>(null);
  const submittingRef = useRef(false);

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [countryPhoneCode, setCountryPhoneCode] = useState<
    string | undefined
  >();
  const [name, setName] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [nationality, setNationality] = useState('MY');
  const [phoneToken, setPhoneToken] = useState<string | undefined>(
    prefill?.phoneVerificationToken ?? undefined
  );
  const [prefillTokenRejected, setPrefillTokenRejected] = useState(false);
  const [emailToken, setEmailToken] = useState<string | undefined>();
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [phraseTouched, setPhraseTouched] = useState(false);
  const [avatarToken, setAvatarToken] = useState<string | null>(null);
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [termsChecked, setTermsChecked] = useState(false);
  const [phoneError, setPhoneError] = useState<PinnedError | null>(null);
  const [emailError, setEmailError] = useState<PinnedError | null>(null);
  const [phraseError, setPhraseError] = useState<PinnedError | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const nameError = name?.trim() ? null : 'Name is required';
  const emailFormatError = !email?.trim()
    ? 'Email is required'
    : EMAIL_PATTERN.test(email)
      ? null
      : 'Please enter a valid email address';
  const phraseRuleError = validateSecurityPhrase(phrase, {
    required: true,
    personal: {
      name,
      phoneNumber,
      countryCode: countryPhoneCode,
      email
    }
  });

  const phoneValue = `${countryPhoneCode ?? ''}:${phoneNumber ?? ''}`;
  const pinned = (error: PinnedError | null, value: string) =>
    error && error.value === value ? error.message : null;

  const canSubmit =
    !!phoneToken &&
    !!emailToken &&
    !nameError &&
    !emailFormatError &&
    !phraseRuleError &&
    !!avatarToken &&
    termsChecked &&
    !createAccount.isPending;

  const handleError = (error: unknown) => {
    if (!(error instanceof RegistrationApiError)) {
      setFormError(GENERIC_ERROR);
      return;
    }
    const message = error.message || GENERIC_ERROR;
    const fields = badFieldsOf(error);
    const forPhone = { message, value: phoneValue };
    const forEmail = { message, value: email ?? '' };
    if (fields.includes('securityAvatarToken')) {
      setAvatarToken(null);
      setAvatarRefreshKey((k) => k + 1);
      setAvatarError(AVATAR_AGAIN);
      return;
    }
    switch (error.code) {
      case 'USER_ALREADY_REGISTERED':
        setPhoneError(forPhone);
        return;
      case 'EMAIL_ALREADY_REGISTERED':
        setEmailError(forEmail);
        return;
      case 'REGISTRATION_PHONE_NOT_VERIFIED':
        setPhoneError(forPhone);
        setPrefillTokenRejected(true);
        formRef.current?.resetPhoneOtp();
        return;
      case 'REGISTRATION_EMAIL_NOT_VERIFIED':
        setEmailError(forEmail);
        formRef.current?.resetEmailOtp();
        return;
      case 'REGISTRATION_VERIFICATION_TOKEN_INVALID':
        if (fields.includes('phoneVerificationToken')) {
          setPhoneError(forPhone);
          setPrefillTokenRejected(true);
          formRef.current?.resetPhoneOtp();
          return;
        }
        if (fields.includes('emailVerificationToken')) {
          setEmailError(forEmail);
          formRef.current?.resetEmailOtp();
          return;
        }
        break;
      case 'SECURITY_PHRASE_PERSONAL_INFO':
        setPhraseError({ message, value: phrase });
        return;
      case 'INVALID_ARGUMENT':
        if (fields.includes('securityPhrase')) {
          setPhraseError({
            message: phraseRuleError ?? message,
            value: phrase
          });
          return;
        }
        break;
      default:
        break;
    }
    setFormError(message);
  };

  const handleSubmit = () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setFormError(null);
    const body: CreateAccountBody = {
      phoneVerificationToken: phoneToken!,
      emailVerificationToken: emailToken!,
      name: name!.trim(),
      email: email!.trim(),
      phoneNumber: phoneNumber!,
      countryCode: countryPhoneCode!,
      nationality,
      securityPhrase: normalizeSecurityPhrase(phrase),
      securityAvatarToken: avatarToken!,
      termsAccepted: true,
      face: { faceIndex: context.faceIndex },
      origin: originFor(context),
      ...(dryRunEnabled && dryRunChecked ? { dry_run: 'true' as const } : {})
    };
    createAccount.mutate(body, {
      onSuccess: (result) =>
        'dryRun' in result ? onDryRun() : onCreated(result),
      onError: handleError,
      onSettled: () => {
        submittingRef.current = false;
      }
    });
  };

  const initialCountryCode = prefill?.countryCode ?? geo.data?.dialCode;
  const initialPhoneNumber = prefill
    ? `${prefill.countryCode}${prefill.phoneNumber}`
    : undefined;

  return (
    <div className="flex min-h-screen flex-col items-center bg-white">
      <img src={logo} alt="Aye Logo" className="mt-9 h-8 w-16 flex-shrink-0" />

      <div className="flex w-full flex-1 flex-col items-center px-6">
        <div className="mt-6 w-full max-w-md">
          <StepBanner tone="final" label="FINAL STEP" text="Almost done!" />
        </div>

        <div className="mt-8 w-full max-w-md">
          <h1 className="text-2xl font-bold">Create your account</h1>
          <p className="text-md mt-2 text-gray-500">
            Takes less than a minute.
          </p>
        </div>

        <AccountDetailsForm
          ref={formRef}
          setPhoneNumber={setPhoneNumber}
          phoneNumberError={() => false}
          setCountryPhoneCode={setCountryPhoneCode}
          nationality={nationality}
          setNationality={setNationality}
          setName={setName}
          setEmail={setEmail}
          name={name}
          email={email}
          nameError={nameError}
          emailError={emailFormatError}
          nameTouched={nameTouched}
          emailTouched={emailTouched}
          setNameTouched={setNameTouched}
          setEmailTouched={setEmailTouched}
          securityPhrase={phrase}
          setSecurityPhrase={setPhrase}
          securityPhraseError={phraseRuleError}
          securityPhraseTouched={phraseTouched}
          setSecurityPhraseTouched={setPhraseTouched}
          initialPhoneNumber={initialPhoneNumber}
          initialCountryCode={initialCountryCode}
          phoneNumber={phoneNumber}
          countryCode={countryPhoneCode}
          onPhoneVerified={setPhoneToken}
          onEmailVerified={setEmailToken}
          onPhoneReset={() => setPhoneToken(undefined)}
          onEmailReset={() => setEmailToken(undefined)}
          preVerifiedPhoneToken={
            prefillTokenRejected
              ? undefined
              : (prefill?.phoneVerificationToken ?? undefined)
          }
          preVerifiedEmailToken={emailToken}
          phoneServerError={pinned(phoneError, phoneValue)}
          emailServerError={pinned(emailError, email ?? '')}
          phraseServerError={pinned(phraseError, phrase)}
          beforePhrase={
            <div>
              <SecurityAvatarPicker
                loadCandidates={registrationApi.getAvatarCandidates}
                required
                value={avatarToken}
                onChange={(token) => {
                  setAvatarToken(token);
                  setAvatarError(null);
                }}
                refreshKey={avatarRefreshKey}
              />
              {avatarError && (
                <p
                  id="security-avatar-error"
                  className="mt-1 text-xs text-red-500"
                >
                  {avatarError}
                </p>
              )}
            </div>
          }
        />

        <div className="w-full max-w-md">
          <div className="mt-6 flex flex-row items-center justify-start">
            <Checkbox
              id="terms-checkbox"
              checked={termsChecked}
              color="success"
              onChange={() => setTermsChecked((v) => !v)}
            />
            <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
              I have read and agree to the&nbsp;
              <a
                href="https://aye-ai.org/user-tc/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                terms and conditions
              </a>
            </label>
          </div>

          {dryRunEnabled && (
            <div className="mt-2 flex flex-row items-center justify-start">
              <Checkbox
                id="dry-run-checkbox"
                checked={dryRunChecked}
                color="success"
                onChange={() => setDryRunChecked((v) => !v)}
              />
              <label
                htmlFor="dry-run-checkbox"
                className="text-sm text-gray-700"
              >
                Run test registration{' '}
                <span className="text-red-700">
                  (User account will not be created)
                </span>
              </label>
            </div>
          )}

          {formError && (
            <p className="mt-3 text-center text-sm text-red-500">{formError}</p>
          )}

          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className={`mt-5 w-full rounded-2xl bg-[#5CB891] px-2 py-4 font-semibold text-white transition-all duration-300 ease-out hover:bg-[#4aa07c] ${
              canSubmit ? '' : 'cursor-not-allowed opacity-50'
            }`}
          >
            {createAccount.isPending ? 'Creating account...' : 'Create account'}
          </button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Encrypted in transit · Your data stays private
          </p>
        </div>
      </div>

      <div className="my-4 flex justify-center">
        <PayNetFooter />
      </div>
    </div>
  );
}
